import { useState, useEffect, useCallback } from 'react';
import { endClientSessionApi } from "../api/application";
import { keepAlive } from '../actions/session';
import { apiPaths } from '../utils/apiUtils';
import { setResetCallback } from '../api/api';

const useIdleSession = ({ warningPeriod = 60} = {}) => {
    const [warningOpen, setWarningOpen] = useState(false);
    const [warningTimeLeft, setWarningTimeLeft] = useState(warningPeriod); // 60-second warning period
    let lastActivity = Date.now();
    let idleSessionTime = 600;
    let logoutFlag = false; // Flag to check if the session logout is triggered

    const setIdleSessionTime = (time: any) => {
        idleSessionTime = time;
    }

    const setLogoutFlag = (value: any) => {
        logoutFlag = value;
    }

    // Fetch and Extend session by calling /keep-alive
    const handleExtendSession = useCallback(async () => {
        try {
            const response = await keepAlive();
            setIdleSessionTime(response.timeoutInSeconds);
            setActivitytime();
            setWarningOpen(false);
        } catch (error) {
            console.error('Failed to extend session:', error);
            handleSessionTimeout();
        }
    }, []);

    // Handle session timeout (logout and redirect)
    const handleSessionTimeout = useCallback(() => {
        setLogoutFlag(true);
        setWarningOpen(false);
        // No identity provider to bounce through; drop the local application
        // reference and return to the start.
        sessionStorage.removeItem("applicationId");
        /*
         * End the server session before reloading.
         *
         * This is a full page load, so React state is discarded either way — but
         * the landing page rebuilds its state from the server session on mount.
         * Leaving that session alive put a timed-out applicant back on the email
         * step, address prefilled, as though nothing had happened. Fire and
         * forget: the reload must not wait on the server, and the helper swallows
         * its own errors.
         */
        void endClientSessionApi();
        window.location.assign(`${window.location.origin}/`);
    }, []);

    const setActivitytime = () => {
        lastActivity = Date.now();
    }

    // Monitor remaining time and show warning
    useEffect(() => {
        const interval = setInterval(() => {
            const remaining = idleSessionTime - Math.ceil(((Date.now()-lastActivity)/1000));
            // Show warning before timeout
            if (remaining <= warningPeriod && remaining > 0 && !logoutFlag) {
                setWarningOpen(true);
                setWarningTimeLeft(remaining);
            } else if(remaining <= 0 || logoutFlag) {
                clearInterval(interval); // Clear the interval to stop checking
                !logoutFlag && handleSessionTimeout(); // Trigger logout
                setLogoutFlag(false); // Reset the logout flag
            } else {
                setWarningOpen(false);
            }
        }, 1000); // Update every second
        return () => clearInterval(interval);
    }, []);

    // Initial fetch of session timeout
    useEffect(() => {
        handleExtendSession();
    }, []);

    useEffect(() => {
        setResetCallback(setActivitytime);
        return () => {
            setResetCallback(() => {}); // Reset the callback when the component unmounts
        };
    }, []);

    return {
        idleSessionTime,
        warningOpen,
        warningTimeLeft,
        handleExtendSession,
        handleSessionTimeout,
        setActivitytime
    };
};

export default useIdleSession;